import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useFeatureFlagsConnection } from "../composables/useFeatureFlagsConnection.js";
import { useObservability } from "../composables/useObservability.js";
export const useFeatureFlags = defineStore("featureFlags", () => {
  const connection = useFeatureFlagsConnection();
  const obs = useObservability();
  const flags = ref({});
  const loading = ref(false);
  const error = ref(null);
  const loadFlags = async () => {
    if (loading.value) return;
    loading.value = true;
    error.value = null;
    try {
      const session = await connection.getSession();
      if (!session) {
        throw new Error("Unable to establish RPC connection");
      }
      const loadedFlags = await session.getAllFlags();
      flags.value = loadedFlags;
      obs.event("feature-flags-loaded", {
        flagCount: Object.keys(loadedFlags).length
      });
    } catch (err) {
      const errorMessage2 = err instanceof Error ? err.message : "Failed to load feature flags";
      error.value = errorMessage2;
      obs.error(err, {
        function: "useFeatureFlags:loadFlags"
      });
    } finally {
      loading.value = false;
    }
  };
  const getFlag = async (key) => {
    try {
      const session = await connection.getSession();
      if (!session) {
        throw new Error("Unable to establish RPC connection");
      }
      const value = await session.getFlag(key);
      flags.value[key] = value;
      return value;
    } catch (err) {
      obs.error(err, {
        function: "useFeatureFlags:getFlag",
        flagKey: key
      });
      return flags.value[key] ?? false;
    }
  };
  const updateFlag = async (key, value) => {
    const prevValue = flags.value[key] ?? false;
    flags.value = { ...flags.value, [key]: value };
    try {
      const session = await connection.getSession();
      if (!session) {
        throw new Error("Unable to establish RPC connection");
      }
      const updatedFlags = await session.updateFlag(key, value);
      flags.value = { ...updatedFlags, [key]: value };
      obs.event("feature-flag-updated", {
        flagKey: key,
        value
      });
    } catch (err) {
      flags.value = { ...flags.value, [key]: prevValue };
      obs.error(err, {
        function: "useFeatureFlags:updateFlag",
        flagKey: key,
        value
      });
      throw err;
    }
  };
  const toggleFlag = async (key) => {
    const prevValue = flags.value[key] ?? false;
    const newValue = !prevValue;
    flags.value = { ...flags.value, [key]: newValue };
    try {
      const session = await connection.getSession();
      if (!session) {
        throw new Error("Unable to establish RPC connection");
      }
      const updatedFlags = await session.updateFlag(key, newValue);
      flags.value = { ...updatedFlags, [key]: newValue };
      obs.event("feature-flag-toggled", {
        flagKey: key,
        newValue
      });
      return newValue;
    } catch (err) {
      flags.value = { ...flags.value, [key]: prevValue };
      obs.error(err, {
        function: "useFeatureFlags:toggleFlag",
        flagKey: key
      });
      throw err;
    }
  };
  const updateFlags = async (updates) => {
    try {
      const session = await connection.getSession();
      if (!session) {
        throw new Error("Unable to establish RPC connection");
      }
      await session.updateFlags(updates);
      Object.assign(flags.value, updates);
      obs.event("feature-flags-bulk-updated", {
        flagCount: Object.keys(updates).length,
        flags: Object.keys(updates)
      });
    } catch (err) {
      obs.error(err, {
        function: "useFeatureFlags:updateFlags",
        flagCount: Object.keys(updates).length
      });
      throw err;
    }
  };
  const initialize = async () => {
    if (!connection.isConnected.value && !connection.isConnecting.value) {
      await connection.connect();
    }
    if (Object.keys(flags.value).length === 0) {
      await loadFlags();
    }
  };
  const isReady = computed(() => connection.isConnected.value && !loading.value);
  const hasError = computed(
    () => Boolean(error.value || connection.error.value)
  );
  const errorMessage = computed(
    () => error.value || connection.error.value || null
  );
  const isEnabled = (key, fallback = false) => {
    return flags.value[key] ?? fallback;
  };
  return {
    // State
    flags,
    loading,
    error: errorMessage,
    isConnected: connection.isConnected,
    isConnecting: connection.isConnecting,
    isReady,
    hasError,
    // Actions
    initialize,
    loadFlags,
    getFlag,
    updateFlag,
    toggleFlag,
    updateFlags,
    isEnabled,
    connect: connection.connect,
    disconnect: connection.disconnect
  };
});
